import { useEffect, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { routes } from 'src/app/routes';

import { useDutchieIntelligenceContext } from '../../context/dutchie-intelligence';
import { useContextRegistration } from '../hooks';

import { buildNavigationContext, buildRoutesListContext } from './context-builders';
import { flattenRoutes } from './helpers';

import type { ContextMetadata } from '../../context/types';

/**
 * Context IDs for navigation data
 */
const NAVIGATION_CONTEXT_IDS = {
  NAVIGATION: 'navigation',
  ROUTES_LIST: 'routes-list',
};

/**
 * Context scope for navigation data
 */
const NAVIGATION_CONTEXT_SCOPE = 'app';

/**
 * Metadata for navigation context (stable reference to prevent re-registration)
 */
const NAVIGATION_METADATA: Partial<ContextMetadata> = {
  type: 'route-data',
  priority: 'medium',
  source: 'route',
  global: true,
};

/**
 * Metadata for routes list context (stable reference to prevent re-registration)
 */
const ROUTES_LIST_METADATA: Partial<ContextMetadata> = {
  type: 'route-data',
  priority: 'low',
  source: 'route',
  global: true,
};

/**
 * Bridge component that automatically registers navigation/routing information as AI context.
 *
 * Provides the AI with:
 * - All available routes/pages
 * - Current location and section
 * - Related/nearby pages
 * - Parent/child/sibling relationships
 * - Route hierarchy
 *
 * This enables the AI to suggest navigation and understand the app structure.
 */
export function NavigationResource() {
  const { setCurrentPage } = useDutchieIntelligenceContext();
  const location = useLocation();

  // Flatten routes once on mount
  const allRoutes = useMemo(() => flattenRoutes(routes), []);

  // Update current page whenever location changes
  useEffect(() => {
    setCurrentPage(location.pathname);
  }, [location.pathname, setCurrentPage]);

  // Register navigation context (updates when location changes)
  useContextRegistration(
    NAVIGATION_CONTEXT_IDS.NAVIGATION,
    () => buildNavigationContext(location.pathname, allRoutes),
    [location.pathname, allRoutes],
    NAVIGATION_CONTEXT_SCOPE,
    NAVIGATION_METADATA
  );

  // Register static routes list (only once on mount)
  useContextRegistration(
    NAVIGATION_CONTEXT_IDS.ROUTES_LIST,
    () => buildRoutesListContext(allRoutes),
    [allRoutes],
    NAVIGATION_CONTEXT_SCOPE,
    ROUTES_LIST_METADATA
  );

  // This component doesn't render anything
  return null;
}
